import {
  useBusinessHours,
  useUpdateBusinessHours,
} from '@/hooks/use-business-hours/use-business-hours';
import { Flex, Spin } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import styles from './business-hours.module.scss';
import { BusinessHoursMobile } from './business-hours-mobile';
import { BusinessHoursDesktop } from './business-hours-desktop';
import { CompanySelectBh } from '../company-select-bh/company-select-bh';
import { useState } from 'react';
import { BusinessHoursModal } from '../business-hours-modal/business-hours-modal';
import Icon, { LoadingOutlined } from '@ant-design/icons';
import InfoIcon from '@/assets/icons/info-circle-icon.svg?react';
import Settings from '@/assets/icons/settings-01.svg?react';
import { getHoursForDate } from '@/utils/helpers';
import { TimezoneInfo } from '../timezone-info/timezone-info';
import { Button } from '../button/button';
import { BusinessHoursPermanent } from '../business-hours-permanent/business-hours-permanent';
import { BusinessHoursDayData } from '../business-hours-day/business-hours-day';
import { useSelectedCompany } from '@/hooks/use-selected-company/use-selected-company';

type TemporaryHoursData = {
  periods: { start: string; end: string }[];
  isClosed: boolean;
  isAllDay: boolean;
};

export function BusinessHours() {
  const { selectedCompany, setSelectedCompany } = useSelectedCompany({
    storageKey: 'selectedCompany_businessHours',
  });
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Dayjs | null>(null);
  const [currentMonth, setCurrentMonth] = useState<Dayjs>(() => dayjs());
  const [isBusinessHoursPermanentVisible, setIsBusinessHoursPermanentVisible] = useState(false);

  const companyId = selectedCompany?.id?.toString();
  const dateParam = currentMonth.startOf('month').format('YYYY-MM-DD');

  const { businessHoursData, isLoading } = useBusinessHours(companyId, dateParam);
  const {
    updateCompanyBusinessHours,
    isPendingUpdateBusinessHours,
    updateCompanyBusinessHoursAsync,
  } = useUpdateBusinessHours(companyId);

  const { permanent = [], temporary = [] } = businessHoursData || {};

  const handleSaveTemporary = (date: Dayjs, data: TemporaryHoursData) => {
    if (!companyId) return;

    updateCompanyBusinessHours({
      company_id: companyId,
      business_hours_params: [
        {
          date: date.format('YYYY-MM-DD'),
          status: 'temporary',
          is_closed: data.isClosed,
          all_day: data.isAllDay,
          note: '',
          periods: data.periods,
        },
      ],
    });
  };

  const handleSavePermanent = (data: BusinessHoursDayData[]) => {
    if (!companyId) return;

    const business_hours_params = data.map((day) => ({
      date: dayjs().format('YYYY-MM-DD'),
      status: 'permanent' as const,
      is_closed: day.isClosed,
      all_day: day.isAllDay,
      weekday: day.weekday,
      note: '',
      periods: day.periods,
    }));

    updateCompanyBusinessHoursAsync({
      company_id: companyId,
      business_hours_params,
    }).then(() => {
      setIsBusinessHoursPermanentVisible(false);
    });
  };

  const handleDateSelect = (date: Dayjs) => {
    setSelectedDate(date);
    setModalOpen(true);
  };

  const getInitialModalData = () => {
    const hoursArray = getHoursForDate(temporary, permanent, selectedDate);
    const firstPeriod = hoursArray[0];

    const extractTime = (isoString: string) => {
      const match = isoString.match(/T(\d{2}:\d{2})/);
      return match ? match[1] : '';
    };

    const periods = hoursArray
      .filter((h) => !h.is_closed && h.start_time && h.end_time)
      .map((h) => ({
        start: extractTime(h.start_time!),
        end: extractTime(h.end_time!),
      }));

    return {
      periods: periods.length > 0 ? periods : undefined,
      isClosed: firstPeriod?.is_closed || false,
      isAllDay: firstPeriod?.all_day || false,
    };
  };

  if (isLoading) {
    return (
      <div className={styles.loadingWrapper}>
        <Spin indicator={<LoadingOutlined spin />} />
      </div>
    );
  }

  const initialModalData = getInitialModalData();

  const handleCloseBusinessHoursPermanentSettings = () => {
    setIsBusinessHoursPermanentVisible(false);
  };

  const handleOpenBusinessHoursPermanentSettings = () => {
    setIsBusinessHoursPermanentVisible(true);
  };

  return (
    <Flex vertical gap={16}>
      <CompanySelectBh value={selectedCompany} onChange={setSelectedCompany} />

      {selectedCompany && (
        <>
          <TimezoneInfo timezone={selectedCompany.time_zone} />
          <Flex className={styles.infoWrapper}>
            <Icon component={() => <InfoIcon className={styles.infoIcon} />} />
            <p className={styles.infoText}>
              Use "Manage Business Hours" to edit the regular weekly schedule for this company.
              Select a specific day to set temporary hours.
            </p>
          </Flex>

          <Button buttonVariant="secondary" onClick={handleOpenBusinessHoursPermanentSettings}>
            <Icon component={() => <Settings className={styles.settingsIcon} />} />
            Manage Business Hours
          </Button>

          <div className={styles.mobileView}>
            <BusinessHoursMobile
              businessHoursData={businessHoursData}
              currentMonth={currentMonth}
              onMonthChange={setCurrentMonth}
              onDateSelect={handleDateSelect}
            />
          </div>

          <div className={styles.desktopView}>
            <BusinessHoursDesktop
              businessHoursData={businessHoursData}
              currentMonth={currentMonth}
              onMonthChange={setCurrentMonth}
              onDateSelect={handleDateSelect}
            />
          </div>

          <BusinessHoursModal
            open={modalOpen}
            onClose={() => setModalOpen(false)}
            selectedDate={selectedDate}
            initialPeriods={initialModalData.periods}
            initialIsClosed={initialModalData.isClosed}
            initialIsAllDay={initialModalData.isAllDay}
            onSave={(data) => selectedDate && handleSaveTemporary(selectedDate, data)}
            isLoadingUpdateBusinessHours={isPendingUpdateBusinessHours}
          />
          <BusinessHoursPermanent
            isSaveLoading={isPendingUpdateBusinessHours}
            isVisible={isBusinessHoursPermanentVisible}
            onClose={handleCloseBusinessHoursPermanentSettings}
            onSave={handleSavePermanent}
            permanentHours={permanent}
          />
        </>
      )}
    </Flex>
  );
}
